import { HttpClient } from '@adapters/HttpClient';
import {
  tryResolveCodPayoutItemRowCurrency,
  tryResolvePayoutRowCurrency,
} from '@modules/CODPayments/utils/resolveCurrencyFromApi';
import { PayoutDetail, PayoutItem, PayoutItemsFilters, PayoutItemsResponse } from '../models';

const mapDetailCurrency = (row: PayoutDetail): PayoutDetail => {
  const extras = row as unknown as Record<string, unknown>;
  const parsed = tryResolvePayoutRowCurrency(extras);
  return {
    ...row,
    ...(parsed !== undefined ? { currency: parsed } : {}),
  };
};

const mapPayoutItemCurrency = (item: PayoutItem): PayoutItem => {
  const extras = item as unknown as Record<string, unknown>;
  const parsed = tryResolveCodPayoutItemRowCurrency(extras);
  return {
    ...item,
    ...(parsed !== undefined ? { currency: parsed } : {}),
  };
};

export class PayoutDetailApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene detalle de un payout específico
   * GET /api/v1/cod-payouts/{id}
   */
  async getPayoutById(id: number): Promise<PayoutDetail> {
    const response = await this.httpClient.get<PayoutDetail>(`/cod-payouts/${id}`);
    return mapDetailCurrency(response);
  }

  /**
   * Obtiene items paginados de un payout específico
   * GET /api/v1/cod-payouts/{id}/items
   */
  async getPayoutItems(payoutId: number, filters: PayoutItemsFilters = {}): Promise<PayoutItemsResponse> {
    const { per_page = 15, page = 1, sort_by, sort_direction, ref, tracking_number, shop_id, payout_type } = filters;

    const params: Record<string, any> = {
      page,
      per_page,
      ...(sort_by ? { sort_by } : {}),
      ...(sort_direction ? { sort_direction } : {}),
      ...(ref ? { ref } : {}),
      ...(tracking_number ? { tracking_number } : {}),
      ...(shop_id ? { shop_id } : {}),
      ...(payout_type ? { payout_type } : {}),
    };

    const response = await this.httpClient.get<PayoutItemsResponse>(
      `/cod-payouts/${payoutId}/items`,
      params,
      {},
      page === 1,
    );

    return {
      ...response,
      data: (response.data ?? []).map(mapPayoutItemCurrency),
    };
  }

  /**
   * Exporta items de un payout específico a Excel
   * POST /api/v1/cod-payouts/{payout_id}/items/export
   */
  async exportPayoutItems(
    payoutId: number,
    filters: Partial<PayoutItemsFilters>,
    columns: string[] = [],
  ): Promise<{ url_file: string }> {
    const { sort_by, sort_direction, tracking_number, ref, shop_id } = filters;

    // Construir query params
    const queryParams: Record<string, any> = {};
    if (sort_by) queryParams['sorting[column]'] = sort_by;
    if (sort_direction) queryParams['sorting[direction]'] = sort_direction;
    if (tracking_number) queryParams.tracking_number = tracking_number;
    if (ref) queryParams.ref = ref;
    if (shop_id) queryParams.shop_id = shop_id;

    // Construir body con columnas
    const body: { columns: string[] } = {
      columns:
        columns.length > 0
          ? columns
          : ['id', 'amount', 'currency', 'order.ref', 'order.tracking_number', 'client.name'],
    };

    // Construir URL con query params
    const queryString = new URLSearchParams(
      Object.entries(queryParams).reduce(
        (acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        },
        {} as Record<string, string>,
      ),
    ).toString();

    const url = `/cod-payouts/${payoutId}/items/export${queryString ? `?${queryString}` : ''}`;

    const response = await this.httpClient.post<{ url_file: string }>(url, body);

    return response;
  }
}
